<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.tagsEditor',
    path: 'tags',
    component: 'TagsEditor',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'TagsEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const tagTypes = ref(null);
  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'tag_type',
      type: 'select',
      header: $fn.value.pgettext('Tag', 'Type'),
      width: '200px',
      required: true,
      options: tagTypes.value,
      valueField: 'value',
      textField: 'name',
    },
    {
      name: 'name',
      header: $fn.value.pgettext('Tag', 'Name'),
      width: '200px',
      required: true,
    },
  ]);

  const $data = computed(() => ({
    tagTypes: tagTypes.value,
    items: items.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_tagTypes = () => {
    return $fn.value.fetch('list_tag_types');
  };
  const $$load_items = () => {
    return $fn.value
      .fetch('get_tags', { parameters: { tag_type: '*' } })
      .then((data) => _.flatten(_.values(data)))
      .then((p) =>
        _.map(p, (pp) =>
          _.extend(pp, {
            id: `${pp.tag_type}:${pp.name}`,
          })
        )
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'tagTypes' },
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_3343864469950044_columns',
        nodeId: '3343864469950044',
        dependsOn: ['tagTypes'],
      },
    ],
    loaders: { tagTypes: $$load_tagTypes, items: $$load_items },
    ux: {},
    dataRefs: {
      tagTypes,
      items,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_j2fgt0ijc_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_3343864469950044_script_hp9wzc8lf_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('tag').upsert(_.omit($event.data.row, ['id']))
    );
  };
  const act_3343864469950044_script_hp9wzc8lf_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_hp9wzc8lf_value_callable($event);
      },
    };
  };
  const act_3343864469950044_script_zejjop5fi_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('tag').delete({ ...$event.data.row })
    );
  };
  const act_3343864469950044_script_zejjop5fi_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_zejjop5fi_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_j2fgt0ijc =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_j2fgt0ijc_getActionArgs,
    });
  const inst_evh_7315092382398562_close_nbk0kwuh6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_j2fgt0ijc,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_nbk0kwuh6],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_yqvo7wfn4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_og9nfav30 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_yqvo7wfn4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_og9nfav30],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_rz37p7qd3 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_8pp9tslz0 =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_rz37p7qd3,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_8pp9tslz0],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_hp9wzc8lf = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_hp9wzc8lf_getActionArgs,
  });
  const inst_evh_3343864469950044_save_xxe8ygaa5 =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_hp9wzc8lf,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_xxe8ygaa5],
    event: 'save',
  });
  const act_3343864469950044_script_zejjop5fi = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_zejjop5fi_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_rxpnxqb6m =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_zejjop5fi,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_rxpnxqb6m],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_nbk0kwuh6,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_og9nfav30,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_8pp9tslz0,
    reload: inst_evh_8320016629450276_clickPreventStop_8pp9tslz0,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_3343864469950044_save_xxe8ygaa5,
    inst_evh_3343864469950044_save,
    inst_evh_3343864469950044_delete_rxpnxqb6m,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_nbk0kwuh6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_nbk0kwuh6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_og9nfav30 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_og9nfav30.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_8pp9tslz0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_8pp9tslz0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_xxe8ygaa5 = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_xxe8ygaa5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_rxpnxqb6m = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_rxpnxqb6m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="TagsEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ gettext('Tags') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.TagsEditor"
        editable
        :value="items"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        v-if="loaded"
        @save="evh_3343864469950044_save($event, {})"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
